import { AuthenticationStrategy } from '../../types/enums/AuthenticationStrategy';
import { FidoEnrollmentEligibility } from '../../types/enums/FidoEnrollmentEligibility';
import { hasUnboundedFidoCredentials, isFidoDeviceBounded } from '../../adapters/fido/utils';
import { isInIframe } from '../../utils/iframe/iframe-utils';
import { AuthenticatorLocation, AuthenticationResult } from '@mc-alberta/types';
import { getAdapters } from './getAdapters';
/**
 *
 * @param isIdentityVerificationRequired
 * @returns {FidoEnrollmentEligibility}
 */
export function getFidoEnrollmentEligibility(isIdentityVerificationRequired = true) {
    // ---------- Start eligibility rules ------------//
    /**
     * Fido enrollment eligibility when there is a passkey (found probabilistically) authenticated
     * successfully by the user before, and at least exist one credential that does not.
     *
     * @param authenticationInstances
     */
    const linkPasskeyExternalRules = (authenticationInstances) => {
        return [
            hasNoUnboundedFidoCredential(authenticationInstances),
            probablyPasskeyFound(authenticationInstances)
        ];
    };
    /**
     * Fido enrollment eligibility when the context is third-party (window.parent is defined)
     * and no passkey is found for any subject,
     * so can create a new one for the first time in a popup.
     *
     * @param authenticationInstances
     */
    const createPasskeyExternalRules = (authenticationInstances) => {
        return [
            isInIframe(),
            hasAFidoMethodWithIdentityVerified(authenticationInstances),
            hasNoBoundedOrUnboundedFidoCredentials(authenticationInstances)
        ];
    };
    /**
     * Fido enrollment eligibility when a passkey is deterministically found,
     * and can be asserted to bind it to the new identity verification.
     *
     * @param authenticationInstances
     */
    const linkPasskeySelfRules = (authenticationInstances) => {
        var _a, _b, _c, _d;
        for (const instance of authenticationInstances) {
            const fidoAdapter = instance.adapters[AuthenticationStrategy.FIDO2];
            const authenticatorLocation = (_c = (_b = (_a = fidoAdapter.authenticationMethod) === null || _a === void 0 ? void 0 : _a.methodAttributes) === null || _b === void 0 ? void 0 : _b.boundMfaMethod) === null || _c === void 0 ? void 0 : _c.authenticatorLocation;
            const isAuthenticated = fidoAdapter.authenticationResult === AuthenticationResult.AUTHENTICATED;
            const hasIdToken = (_d = fidoAdapter.bindingTokens) === null || _d === void 0 ? void 0 : _d.idToken;
            if (isAuthenticated &&
                authenticatorLocation === AuthenticatorLocation.THIS_DEVICE &&
                hasIdToken) {
                // assume the idToken has been "silently bound" during deterministic FIDO authentication
                return [false];
            }
        }
        return [definitelyPasskeyFound(authenticationInstances)];
    };
    /**
     * Fido enrollment eligibility when the context is first-party (no window.parent is defined)
     * and no passkey is found for any subject,
     * so can create a new one for the first time in the current UI.
     *
     * @param authenticationInstances
     */
    const createPasskeySelfRules = (authenticationInstances) => {
        return [
            !isInIframe(),
            hasAFidoMethodWithIdentityVerified(authenticationInstances),
            hasNoBoundedOrUnboundedFidoCredentials(authenticationInstances)
        ];
    };
    // ---------- End eligibility rules ------------//
    /**
     * Provides a list of Fido enrollment eligibility along with a set of requirements that must be
     * fulfilled.
     *
     * @param authenticationInstances
     */
    function enrollmentEligibilities(authenticationInstances) {
        return {
            [FidoEnrollmentEligibility.LINK_PASSKEY_EXTERNAL_UI]: linkPasskeyExternalRules(authenticationInstances),
            [FidoEnrollmentEligibility.CREATE_PASSKEY_EXTERNAL_UI]: createPasskeyExternalRules(authenticationInstances),
            [FidoEnrollmentEligibility.LINK_PASSKEY_SELF_UI]: linkPasskeySelfRules(authenticationInstances),
            [FidoEnrollmentEligibility.CREATE_PASSKEY_SELF_UI]: createPasskeySelfRules(authenticationInstances),
            [FidoEnrollmentEligibility.NONE]: []
        };
    }
    /**
     * At least one of the AuthenticationInstances has a FIDO strategy having a boundMfaMethod
     * with authenticatorLocation as PROBABLY_THIS_DEVICE.
     *
     * @param authenticationInstances
     */
    function probablyPasskeyFound(authenticationInstances) {
        return authenticationInstances.some((instance) => {
            const fidoAdapter = instance.adapters[AuthenticationStrategy.FIDO2];
            const { authenticationResult } = fidoAdapter;
            const probablyKeyFound = getFidoAuthenticationMethods(instance).some((method) => {
                var _a, _b;
                const authenticatorLocation = (_b = (_a = method.methodAttributes) === null || _a === void 0 ? void 0 : _a.boundMfaMethod) === null || _b === void 0 ? void 0 : _b.authenticatorLocation;
                return (isFidoDeviceBounded(method) &&
                    authenticatorLocation === AuthenticatorLocation.PROBABLY_THIS_DEVICE);
            });
            return probablyKeyFound && authenticationResult === AuthenticationResult.AUTHENTICATED;
        });
    }
    /**
     * At least one of the AuthenticationInstances has identity verified
     * and its FIDO strategy has a FIDO authenticationMethod
     * with no boundMfaMethod whose authenticatorLocation is PROBABLY_THIS_DEVICE.
     *
     * @param authenticationInstances
     */
    function definitelyPasskeyFound(authenticationInstances) {
        return authenticationInstances.some((instance) => {
            const definitelyKeyFound = getFidoAuthenticationMethods(instance).some((method) => {
                var _a, _b;
                const authenticatorLocation = (_b = (_a = method.methodAttributes) === null || _a === void 0 ? void 0 : _a.boundMfaMethod) === null || _b === void 0 ? void 0 : _b.authenticatorLocation;
                return (hasUnboundedFidoCredentials(method) ||
                    (authenticatorLocation &&
                        authenticatorLocation !== AuthenticatorLocation.PROBABLY_THIS_DEVICE));
            });
            return isVerificationFulfilled(instance) && definitelyKeyFound;
        });
    }
    /**
     * At least one of the AuthenticationInstances has identity verified,
     * and its FIDO strategy has a FIDO authenticationMethod.
     *
     * @param authenticationInstances
     */
    function hasAFidoMethodWithIdentityVerified(authenticationInstances) {
        return authenticationInstances.some((instance) => {
            const fidoAdapter = instance.adapters[AuthenticationStrategy.FIDO2];
            return isVerificationFulfilled(instance) && fidoAdapter.authenticationMethods.length > 0;
        });
    }
    /**
     * None of the AuthenticationInstances have its FIDO strategy with a boundMfaMethod or
     * unboundMfaMethod.
     *
     * @param authenticationInstances
     */
    function hasNoBoundedOrUnboundedFidoCredentials(authenticationInstances) {
        return authenticationInstances.every((instance) => getFidoAuthenticationMethods(instance).every((method) => !isFidoDeviceBounded(method) && !hasUnboundedFidoCredentials(method)));
    }
    /**
     * None of the FIDO adapters in any AuthenticationInstance has an unboundMfaMethod present.
     *
     * @param authenticationInstances
     */
    function hasNoUnboundedFidoCredential(authenticationInstances) {
        return authenticationInstances.every((instance) => getFidoAuthenticationMethods(instance).every((method) => !hasUnboundedFidoCredentials(method)));
    }
    /**
     * Returns the list of FIDO authentication methods from an authenticationInstance.
     *
     * @param authenticationInstance
     */
    function getFidoAuthenticationMethods(authenticationInstance) {
        const fidoAdapter = authenticationInstance.adapters[AuthenticationStrategy.FIDO2];
        const fidoAuthenticationMethods = fidoAdapter.authenticationMethods;
        return fidoAuthenticationMethods;
    }
    /**
     *
     * @param authenticationInstance
     */
    function isVerificationFulfilled(authenticationInstance) {
        if (isFidoDeviceBounded(authenticationInstance.adapters[AuthenticationStrategy.FIDO2].authenticationMethod)) {
            return false;
        }
        if (!isIdentityVerificationRequired) {
            return true;
        }
        return getAdapters(authenticationInstance).some((adapter) => adapter.isIdentityVerificationPerformed);
    }
    /**
     * @param authenticationInstances
     * @returns {FidoEnrollmentEligibility}
     */
    return function getFidoEnrollmentEligibilityFactor(authenticationInstances) {
        if (!authenticationInstances.length)
            return FidoEnrollmentEligibility.NONE;
        const eligibilities = enrollmentEligibilities(authenticationInstances);
        for (const key in eligibilities) {
            const eligibility = key;
            const eligibilityRules = eligibilities[eligibility];
            const rules = eligibilityRules.map((result) => result);
            const validRules = rules.every(Boolean);
            if (validRules)
                return eligibility;
        }
        return FidoEnrollmentEligibility.NONE;
    };
}
